import { DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { ChartOptions, ChartData } from 'chart.js';
import { SuperAdminService } from '@core/services/super-admin.service';
import { ActivatedRoute, Params } from '@angular/router';
import { ToastService } from '@shared/services/toast.service';
import { BreadcrumbItem, CompanyService } from '@shared/services/company.service';
import { BOOKING_STATUS_LABELS, DATE_RANGE_MESSAGE, END_DATE_GREATER, getMonthAbbreviation, itemsPerPageList, PAYMENT_MODE, SPOT_TYPE_LABEL, USER_ROLES } from '@shared/constant/shared.constant';
import { autoUnsubWith, Utility } from '@shared/services/utility.abstract';
import { RemoveDecimalPipe } from '@shared/custom-date-pipe';
import { TransactionService } from '@core/services/transaction.service';
import { TransactionData } from '@core/models/transaction.model';
import { PaymentMode } from '@core/models/api-response.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DigitFormatPipe } from '@shared/pipes/digits-format.pipe';
import { ItemPerPage } from '@core/models/file-response.model';
import { CommonFunctions } from '@shared/common-functions';
import { EncryptionStorageService } from '@core/services/encryption-storage.service';

@Component({
  selector: 'app-financial',
  templateUrl: './financial.component.html',
  styleUrls: ['./financial.component.scss']
})
export class FinancialComponent implements OnInit{
  readonly destroyRef = inject(DestroyRef);
  transactionlist: TransactionData[] = [];
  role: string = '';
  exportFile: any
  startdate: string = '';
  enddate: string = '';
  account: string = "assets/images/dummy-img.png";
  @Input() outlined: boolean = false;
  chartOptions: ChartOptions;
  revenueData: ChartData;
  dateRangeMessage : string = DATE_RANGE_MESSAGE
  totalItems: number = 0;
  itemsPerPage: number = 10;
  pageNumber: number = 1;
  first: number = 0;
  DateSlot = [];
  financialType = [];
  bookingTypeOption = [];
  companyId: string;
  showPicker: boolean = true;
  id: string = '';
  companyList: any;
  searchForm: FormGroup;
  minDate : Date;
  dropdownOptions: { label: string; value: any }[] = [];
  selectedCompanies: any[] = [];
  companyListWithAll: any[];
  showAsterisk: boolean;
  showParkingLotAsterisk: boolean = false;
  spotTypeMsg: string = SPOT_TYPE_LABEL;
  paymentStatus: { name: string; value: number }[] = [];
  hideAdvanceFilter: boolean = true;
  parkingLotByStatesList: any = [];
  parkingLotByStatesListWithAll: any[];
  CustomItemPerPageBy: ItemPerPage[] = itemsPerPageList;
  SearchItemPerPageBy: any;
  bookingStatusLabel = BOOKING_STATUS_LABELS;
  currentFilters: any = {};

  constructor(
    private readonly transactionService: TransactionService,
    private readonly superAdminService: SuperAdminService,
    private readonly toastService: ToastService,
    private readonly datePipe: DatePipe,
    private readonly companyService: CompanyService,
    private readonly route: ActivatedRoute,
    private readonly fb: FormBuilder,
    private readonly encryptStorage: EncryptionStorageService
  ) { }

  ngOnInit() {
    window.scroll(0,0);
    this.role = this.encryptStorage.getItem('roleName') || '';
    this.companyId = this.encryptStorage.getItem('companyId');
    const breadcrumbObj: BreadcrumbItem[] = [
      {
        title: 'Financials',
      },
    ];
    this.initializeForm();
    this.initializeOptions();
    this.onDateSlotChange(history?.state?.dateRange ? history?.state?.dateRange : {value: 1});
    this.route.params.subscribe((params: Params) => {
      this.id = params['id'];
      this.companyService.setAdminHeaders('0', breadcrumbObj);
      this.isSuperAdmin() ? this.getCompanyList(true) : this.getParkingLotByStatesName(true);
    })
  }

  initializeOptions() {
    this.paymentStatus = [
      { name: 'All', value: 0 },
      { name: 'Received', value: 1 },
      { name: 'Refunded', value: 2 },
    ];
    this.DateSlot = [
      { name: 'Custom', value: 1, flag: true },
      { name: 'Last Month', value: 30, flag: false },
      { name: 'Last 3 Months', value: 90, flag: false },
      { name: 'Last 6 Months', value: 180, flag: false },
      { name: 'Last 13 Months', value: CommonFunctions.getDaysForMonths(13) },
    ]
    const dateRangeValue = history?.state?.dateRange?.value;
    const selectedSlot = dateRangeValue ? this.DateSlot.find(slot => slot.value === dateRangeValue) : null;

    if (selectedSlot) {
      this.searchForm.controls['dateRange'].setValue(selectedSlot.value);
    }
    this.bookingTypeOption = [
      { name: 'All', value: null },
      { name: 'One Time', value: 0 },
      { name: 'Recurring', value: 2 },
      {name: 'Custom Invoice', value: 3}
    ]
  }
  isSuperAdmin(): boolean {
    return this.role === USER_ROLES.SUPER_ADMIN;
  }
  initializeForm() {
    this.searchForm = this.fb.group({
      companies: [[]],
      parkingLots: [[]],
      startDate: [''],
      endDate: [''],
      name: [''],
      bookingType: [null],
      paymentStatus: [0],
      invoiceNumber: [''],
      dateRange: [1],

    });
  }

  onBookingTypeChange(event) {
    this.searchForm.controls['bookingType'].setValue(event);
    this.search();
  }
  onDropdownChange(event: any) {
    this.itemsPerPage = event.value;
    this.pageNumber = 1;
    this.first = 0;
    CommonFunctions.scrollTopRow('top');
    this.search();
  }

  onDateSlotChange(event) {
    const selectedOption = this.DateSlot.find(option => option.value === event.value);
    if (!selectedOption) return;

    const today = new Date();
    let startDate: Date;
    let endDate: Date;
    if (history.state?.checkStartDate && history.state?.checkEndDate) {
      startDate = history.state.checkStartDate;
      endDate = history.state.checkEndDate;
      this.minDate = history.state.startDate;
    } else {
      const previousMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      endDate = new Date(previousMonth);
      switch (selectedOption.value) {
        case 1:
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          endDate = today;
          break;
        case 30:
          startDate = new Date(previousMonth.getFullYear(), previousMonth.getMonth(), 1);
          break;
        case 90:
          startDate = new Date(previousMonth.getFullYear(), previousMonth.getMonth() - 2, 1);
          break;
        case 180:
          startDate = new Date(previousMonth.getFullYear(), previousMonth.getMonth() - 5, 1);
          break;
        default:
          const months = 13;
          const startCalc = new Date(previousMonth);
          startCalc.setMonth(startCalc.getMonth() - (months - 1));
          startDate = new Date(startCalc.getFullYear(), startCalc.getMonth(), 1);
          break;
      }
      this.minDate = startDate;
    }
    this.searchForm.controls['startDate'].setValue(startDate);
    this.searchForm.controls['endDate'].setValue(endDate);
    this.showPicker = selectedOption.flag;
    if (!this.isSuperAdmin() && !this.showPicker) {
      this.hideAdvanceFilter = true;
    }
  }

  toggleHideAdvanceFilter() {
    this.hideAdvanceFilter = !this.hideAdvanceFilter;
  }

  formatDateToString(date : Date) : string {
    return this.datePipe.transform(date, 'yyyy-MM-dd');
  }
    getTimeZoneAbbreviation(timeZone: string): string {
    return CommonFunctions.getTimeZoneAbbreviation(timeZone);
  }

 async reset() {
  history.replaceState(null, '');
  this.searchForm.controls['bookingType'].setValue(null);
  this.searchForm.controls['invoiceNumber'].setValue('');
  this.searchForm.controls['name'].setValue('');
  this.searchForm.controls['paymentStatus'].setValue(0);
  this.searchForm.controls['dateRange'].setValue(1);
  await this.onDateSlotChange({value: 1});
  await this.isSuperAdmin() ? this.getCompanyList(true) : this.getParkingLotByStatesName(true);
    this.pageNumber = 1;
    this.first = 0;
  }

  searchButton() {
    this.pageNumber = 1;
    this.first = 0;
    this.search();
  }

  startDateChange(event) {
    this.searchForm.controls['startDate'].setValue(event);
    this.minDate = event;
  }
  search() {
    const startDateForm = this.searchForm.value.startDate;
    const  endDateForm = this.searchForm.value.endDate;
    if (startDateForm && endDateForm) {
      const startDate = new Date(startDateForm);
      const endDate = new Date(endDateForm);
      this.searchForm.patchValue({
        startDate:startDate,
        endDate: endDate
      });

      if (startDate > endDate) {
        this.toastService.showToastMessage('error', 'Error', END_DATE_GREATER);
        return;
      }

    }

    if (this.isSuperAdmin() && this.searchForm.value.companies.length === 0) {
      this.showAsterisk = true;
      return;
    }
    if (this.searchForm?.value?.parkingLots?.length === 0) {
      this.showParkingLotAsterisk = true;
      return;
    }
    this.currentFilters = {
      companyId: this.searchForm.value.companies.map(company => company.id).filter(id => id !== null && id !== undefined),
      startDateTime: this.formatDateToString(this.searchForm.value.startDate),
      endDateTime: this.formatDateToString(this.searchForm.value.endDate),
      timeZone: Utility.getLocalTimeZone(),
      pageNumber: this.pageNumber,
      pageSize: this.itemsPerPage,
      parkinglotIds: this.searchForm?.value?.parkingLots?.map(item => item.id).filter(id => id !== null && id !== undefined),
      transactionType: this.searchForm.value.paymentStatus,
      financialType: this.searchForm.value.bookingType,
      name: this.searchForm.value.name,
      invoiceNumber: this.searchForm.value.invoiceNumber,
    };
    this.fetchData();
  }
  fetchData() {
    const req = {
      ...this.currentFilters,
      pageNumber: this.pageNumber,
      pageSize: this.itemsPerPage,
    };

    this.getTransactionList(req);
    this.getTransactionOverview();
  }

  getTransactionsexport() {
    const reqforExport = {
      companyId: this.searchForm.value.companies.map(company => company.id).filter(id => id !== null && id !== undefined),
      startDateTime: this.formatDateToString(this.searchForm.value.startDate),
      endDateTime: this.formatDateToString(this.searchForm.value.endDate),
      timeZone: Utility.getLocalTimeZone(),
      pageNumber: 0,
      pageSize: 0,
      parkinglotIds: this.searchForm?.value?.parkingLots?.map(item => item.id).filter(id => id !== null && id !== undefined),
      transactionType: this.searchForm.value.paymentStatus,
      financialType: this.searchForm.value.bookingType,
      name: this.searchForm.value.name,
      invoiceNumber: this.searchForm.value.invoiceNumber,
    };

    const method = this.isSuperAdmin() ? 'getTransactionListForSuperAdmin' : 'getTransactionsByOwner';
    const service = this.isSuperAdmin() ? this.superAdminService : this.transactionService;
    service[method](reqforExport)
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
        next: (response) => {

          this.exportFile = response;
          this.export();
        }
      })
  }

  getTransactionList(req) {
    const method = this.isSuperAdmin() ? 'getTransactionListForSuperAdmin' : 'getTransactionsByOwner';
    const service = this.isSuperAdmin() ? this.superAdminService : this.transactionService;
    service[method](req)
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
      next: (response: any) => {
        this.startdate = this.searchForm.value.startDate;
        this.enddate = this.searchForm.value.endDate;
        this.transactionlist = response.transactionList;
        this.totalItems = response.totalRecords;

      }
    })
  }

  async getCompanyList(initial: boolean = false) {
    const searchPayload = {
      search: '',
      sort: 1
    };

    const response = await this.superAdminService.getCompanyList(searchPayload).toPromise();
    this.companyList = response;
    const selectedCompany = history.state?.selectedCompany;
    const selectedIds = Array.isArray(selectedCompany) ? selectedCompany : [selectedCompany];
    const validSelectedLots = this.companyList.filter(lot => selectedIds.includes(lot.id));
    this.searchForm.controls['companies'].setValue(
      validSelectedLots.length ? validSelectedLots : [...this.companyList]
    );
    await this.getParkingLotByStatesName(initial);
    this.search();
  }

  getParkingLotByStatesName(initial: boolean = false) {
    const req = {
      companyIds: this.isSuperAdmin()
        ? this.searchForm.value.companies.filter(company => company?.id).map(company => company.id)
        : [this.companyId]
    };
    this.superAdminService.getParkingLotNamesByCompanyIds(req)
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
      next: (response: any) => {
        this.parkingLotByStatesList = response.data;
        this.parkingLotByStatesListWithAll = [...this.parkingLotByStatesList];
        const selectedParkingLot = history.state?.selectedParkingLot;
        const selectedIds = Array.isArray(selectedParkingLot) ? selectedParkingLot : [selectedParkingLot];
        const validSelectedLots = this.parkingLotByStatesList.filter(lot => selectedIds.includes(lot.id));
        this.searchForm.controls['parkingLots'].setValue(
          validSelectedLots.length ? validSelectedLots : [...this.parkingLotByStatesList]
        );
        this.showParkingLotAsterisk = false;
        if (initial) {
          this.search();
        }
      },
      error: (_error: any) => {
        this.parkingLotByStatesListWithAll = [];
        this.searchForm.controls['parkingLots'].setValue([]);
      },
    });
  }

  getTransactionOverview() {
    const req = {
      companyId: this.isSuperAdmin() ? this.searchForm.value.companies.map(company => company.id).filter(id => id !== null && id !== undefined) : null,
      startDate: this.formatDateToString(this.searchForm.value.startDate),
      endDate: this.formatDateToString(this.searchForm.value.endDate),
      name: this.searchForm.value.name,
      paymentStatus: this.searchForm.value.paymentStatus,
      financialType: this.searchForm.value.bookingType,
      parkinglotIds: this.searchForm?.value?.parkingLots?.map(item => item.id).filter(id => id !== null && id !== undefined),
      invoiceNumber: this.searchForm.value.invoiceNumber,
      timeZone: Utility.getLocalTimeZone(),
    }
    this.transactionService.getTransActionBySuperAdmin(req)
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
      next: (response: any) => {
        this.createRevenueChart(response.data);
      }
    });
  }

  createRevenueChart(data: any[]) {
    const myCustomPipe = new DigitFormatPipe();
    const labels = data.map((item) => getMonthAbbreviation(item.month));
    const receivedData = [];
    const refundedData = [];
    data.forEach((item) => {
      receivedData.push(item.revenue.received > 0 ? item.revenue.received : 0);
      refundedData.push(item.revenue.refunded > 0 ? item.revenue.refunded : 0);
    });
    this.revenueData = {
      labels: labels,
      datasets: [
        {
          label: 'Received',
          data: receivedData,
          backgroundColor: 'rgba(78, 122, 245, 1)',
          borderColor: 'rgb(78, 122, 245, 1)',
          borderWidth: 1,
          maxBarThickness: 30,
          pointRadius: 6, // Default size
          pointHoverRadius: 6, // Enlarges on hover
          pointBackgroundColor: 'rgb(78, 122, 245, 1)', // Outer circle
          pointBorderColor: '#ffffff', // Inner circle
          pointBorderWidth: 6,
          pointHoverBackgroundColor: 'rgba(78, 122, 245, 1)', // Inner circle changes on hover
          pointHoverBorderColor: '#ffffff', // Outer circle on hover
          pointHoverBorderWidth: 6,
          clip : false
        },
        {
          label: 'Refunded',
          data: refundedData,
          backgroundColor: 'rgba(146, 149, 151, 1)',
          borderColor: 'rgb(146, 149, 151, 1)',
          borderWidth: 1,
          maxBarThickness: 30,
          pointRadius: 6, // Default size
          pointHoverRadius: 6, // Enlarges on hover
          pointBackgroundColor: 'rgba(146, 149, 151, 1)', // Outer circle
          pointBorderColor: '#ffffff', // Inner circle
          pointBorderWidth: 6,
          pointHoverBackgroundColor: 'rgba(146, 149, 151, 1)', // Inner circle changes on hover
          pointHoverBorderColor: '#ffffff', // Outer circle on hover
          pointHoverBorderWidth: 6,
          clip : false
        },
      ],
    };
    const getOrCreateTooltip = CommonFunctions.getOrCreateTooltip();

    const externalTooltipHandler = (context) => {
      const { chart, tooltip } = context;
      const tooltipEl = getOrCreateTooltip(chart);

      if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = 0;
        return;
      }

      if (tooltip.body) {
        const titleLines = tooltip.title || [];
        const bodyLines = tooltip.body.map((b) => b.lines);
        const tableHead = document.createElement('thead');

        titleLines.forEach((title) => {
          const tr = document.createElement('tr');
          tr.style.borderWidth = '0';

          const th = document.createElement('th');
          th.style.borderWidth = '0';
          th.style.textAlign = "center";
         const year = data[tooltip.dataPoints[0].dataIndex].year;

          const titleText = title + ', ' + year;
          const text = document.createTextNode(titleText);

          th.appendChild(text);
          tr.appendChild(th);
          tableHead.appendChild(tr);
        });

        const tableBody = document.createElement('tbody');
        bodyLines.forEach((body, i) => {
          const colors = tooltip.labelColors[i];
          const span = document.createElement('span');
          span.style.color = colors.borderColor;
          span.style.display = "inline-block";
          span.style.minWidth = "60px";
          span.style.textAlign = "right";
          const tr = document.createElement('tr');
          tr.style.backgroundColor = 'inherit';
          tr.style.borderWidth = '0';

          const td = document.createElement('td');
          td.style.borderWidth = '0';
          td.style.padding = "6px 12px";
          td.style.textAlign = "left";
          let colonIndex = body[0]?.indexOf(':');
          let extractedString = body[0]?.substring(0, colonIndex);
          const label = extractedString;
          const modifiedBody = body.map((line) => {
            const [beforeColon, afterColon] = line.split(':');
            const extractedString = afterColon.trim();
            return '$' + extractedString;
          });
          modifiedBody.forEach((modifiedLine) => {
            const td1 = document.createElement('td');
            td1.style.borderWidth = '0';
            td1.style.padding = "6px 12px";
            td1.style.textAlign = "right";
            const labelNode = document.createTextNode(label);
            span.innerHTML = modifiedLine;
            const svg = document.createElementNS(
              'http://www.w3.org/2000/svg',
              'svg'
            );
            svg.setAttribute('width', '20');
            svg.setAttribute('height', '20');
            td.style.display = "flex";
            td.style.alignItems = "center";
            td.style.gap = "6px";
            svg.style.width = "14px";
            svg.style.height = "14px";
            svg.style.flexShrink = "0";
            span.style.display = "inline-block";
            if (labelNode.data === 'Received') {
              svg.innerHTML =
                '<path d="M12.0991 11.2591L9.72042 12.2388C9.61527 12.2821 9.49723 12.2818 9.39226 12.2381C9.28729 12.1944 9.20399 12.1107 9.16069 12.0056C9.11738 11.9004 9.11762 11.7824 9.16135 11.6774C9.20508 11.5725 9.28872 11.4892 9.39386 11.4459L10.8155 10.8603L6.17329 8.92679C6.06844 8.88295 5.98528 8.79929 5.94209 8.69417L4.70345 5.68668L0.389599 3.88961C0.286587 3.84458 0.205382 3.7609 0.163474 3.65658C0.121567 3.55226 0.122309 3.43565 0.165541 3.33187C0.208774 3.22809 0.291037 3.14544 0.394613 3.10173C0.49819 3.05802 0.614793 3.05673 0.719307 3.09815L5.19716 4.96354C5.30213 5.00725 5.38544 5.09087 5.42876 5.19599L6.66723 8.20309L11.1451 10.0685L10.5597 8.64719C10.5164 8.54205 10.5167 8.424 10.5604 8.31903C10.6041 8.21406 10.6878 8.13077 10.7929 8.08746C10.898 8.04416 11.0161 8.04439 11.1211 8.08812C11.226 8.13185 11.3093 8.21549 11.3526 8.32064L12.3323 10.6994C12.3756 10.8045 12.3754 10.9225 12.3316 11.0275C12.2879 11.1325 12.2043 11.2158 12.0991 11.2591Z" fill="#4E7AF5"/>';
              span.style.color = 'rgba(78, 122, 245, 1)';
            } else {
              svg.innerHTML =
                '<path d="M12 0.42876V3.00132C12 3.11503 11.9548 3.22409 11.8744 3.3045C11.794 3.3849 11.685 3.43008 11.5712 3.43008C11.4575 3.43008 11.3485 3.3849 11.2681 3.3045C11.1877 3.22409 11.1425 3.11503 11.1425 3.00132V1.46379L7.58678 5.01992C7.50632 5.10017 7.39729 5.1452 7.28364 5.14511H4.03107L0.726624 8.44956C0.645759 8.52767 0.537453 8.57088 0.425034 8.56991C0.312615 8.56893 0.205076 8.52384 0.125581 8.44434C0.0460853 8.36485 0.000993102 8.25731 1.62082e-05 8.14489C-0.000960686 8.03247 0.0422558 7.92416 0.120358 7.8433L3.55043 4.41322C3.63082 4.33281 3.73986 4.28762 3.85357 4.2876H7.10571L10.5358 0.857519H8.99868C8.88497 0.857519 8.77591 0.812346 8.6955 0.731938C8.6151 0.65153 8.56992 0.542474 8.56992 0.42876C8.56992 0.315045 8.6151 0.205989 8.6955 0.125581C8.77591 0.0451728 8.88497 0 8.99868 0H11.5712C11.685 0 11.794 0.0451728 11.8744 0.125581C11.9548 0.205989 12 0.315045 12 0.42876Z" fill="#B0B0B0"/>';
              span.style.color = 'rgba(146, 149, 151, 1)';
            }
            svg.style.verticalAlign = 'middle'; // Align the SVG to the middle of the text
            span.style.verticalAlign = 'middle'; // Align the text to the middle
            td.appendChild(svg);
            td.appendChild(labelNode);
            tr.appendChild(td);
            td1.appendChild(span);
            tr.appendChild(td1);
            tableBody.appendChild(tr);
          });
        });

        const tableRoot = tooltipEl.querySelector('table');

        while (tableRoot.firstChild) {
          tableRoot.firstChild.remove();
        }

        tableRoot.appendChild(tableHead);
        tableRoot.appendChild(tableBody);
      }

      const positionX = chart.canvas.offsetLeft; // Shift tooltip slightly left
      const positionY = chart.canvas.offsetTop; // Shift tooltip slightly upwards

      const totalBars = chart.data.labels.length; // Total bars in chart
      const tooltipIndex = tooltip.dataPoints[0].dataIndex; // Current hovered bar index

      tooltipEl.style.opacity = 1;

      // Check if the tooltip is for the last 3 bars
      if (tooltipIndex === 0) {
        // Always open the first tooltip to the right
        tooltipEl.style.left = positionX + tooltip.caretX + 'px';
      } else if (tooltipIndex >= totalBars - 3) {
          // Shift tooltip to the left
          tooltipEl.style.left = positionX + tooltip.caretX - tooltipEl.offsetWidth - 10 + 'px';
      } else {
          // Default position
          tooltipEl.style.left = positionX + tooltip.caretX + 'px';
      }

      tooltipEl.style.top = positionY + tooltip.caretY + 'px';
      tooltipEl.style.font = tooltip.options.bodyFont.string;
      tooltipEl.style.paddingLeft = tooltip.options.padding + 5 + 'px';
      tooltipEl.style.paddingRight = tooltip.options.padding + 5 + 'px';
      tooltipEl.style.paddingBottom = tooltip.options.padding + 1 + 'px';
      tooltipEl.style.paddingTop = tooltip.options.padding + 1 + 'px';
    };
    const isSingleMonth = labels.length === 1;

    this.chartOptions = CommonFunctions.chartOptionsForGraph(isSingleMonth, externalTooltipHandler, myCustomPipe, 18);
  }

formatDateString(dateString) {
    let date = new Date(dateString);
    let day = String(date.getDate()).padStart(2, '0');
    let month = String(date.getMonth() + 1).padStart(2, '0');
    let year = date.getFullYear();
    let hours = String(date.getHours()).padStart(2, '0');
    let minutes = String(date.getMinutes()).padStart(2, '0');
    let formattedDate = `${day}-${month}-${year} ${hours}:${minutes}`;

    return formattedDate;
  }

  export() {
    const myCustomPipe = new RemoveDecimalPipe();
    if (this.exportFile) {
      const headers = [
        { header: 'S.No', key: 'index', formatter: (value) => value + 1 },
        { header: 'Name', key: 'userType', formatter: (_value, item) => item.userType === 'Group Operator' ? item.groupCompanyName : item.driverName },
        { header: 'Email', key: 'driverEmail', formatter: (value) => value ? value : '-' },
        { header: 'Mobile Number', key: 'phoneNumber', formatter: (value) => value ? value : '-' },
        { header: 'User Type', key: 'userType', formatter: (value) => value ? value : '-' },
        { header: 'Company Name', key: 'companyName', formatter: (value) => value ? value : '-' },
        { header: 'Parking Location', key: 'parkingLotName', formatter: (value) => value ? value : '-' },
        { header: 'Parking Address', key: 'parkingLotAddress', formatter: (value) => value ? value : '-' },
        { header: 'Spot Type', key: 'equipmentType', formatter: (value) => value ? value : '-' },
        { header: 'Spot Count', key: 'spotCount', formatter: (value) => value > 0 ? value : '-' },
        { header: 'LP Number', key: 'licenseNumberPlate', formatter: (value) => value ? value : '-' },
        { header: 'Type', key: 'type', formatter: (value) => value ? value : '-' },
        { header: 'Arrival Date & Time', key: 'startDate', formatter: (value) => value ? new Date(value) : '-' },
        { header: 'Departure Date & Time', key: 'endDate', formatter: (value) => value ? new Date(value) : '-' },
        { header: 'Time Zone', key: 'timeZone', formatter: (value) => value ? this.getTimeZoneAbbreviation(value) : '-' },
        { header: 'Invoice No.', key: 'invoiceNumber', formatter: (value) => value ? value : '-' },
        { header: 'Invoice Date', key: 'invoiceDate', formatter: (value) => value ? new Date(value) : '-' },
        { header: 'Created Date', key: 'createdDate', formatter: (value) => value ? new Date(value) : '-' },
        { header: 'Base Amount ($)', key: 'amount', formatter: (value) => value ? myCustomPipe.transform(value) : '-' },
        { header: 'Discount ($)', key: 'couponDiscount', formatter: (_value, item) => (item.discount + item.couponDiscount) > 0 ? myCustomPipe.transform(item.discount + item.couponDiscount) : '-', },
        { header: 'Taxes ($)', key: 'taxAndFees', formatter: (value) => value > 0 ? myCustomPipe.transform(value) : '-' },
        { header: 'Credits ($)', key: 'usedCredit', formatter: (value) => value > 0 ? myCustomPipe.transform(value) : '-' },
        { header: 'Transaction Fee ($)', key: 'transactionFees', formatter: (value) => value > 0 ? myCustomPipe.transform(value) : '-' },
        { header: 'Exempt Taxes And Fees ($)', key: 'exemptTaxesAndFees', formatter: (value) => value ? myCustomPipe.transform(value) : '-' },
        { header: 'Received ($)', key: 'receivedAmount', formatter: (value) => value ? myCustomPipe.transform(value) : '-' },
        { header: 'Net Amount ($)', key: 'amountWithoutTransactionFees', formatter: (value) => value ? myCustomPipe.transform(value) : '-' },
        { header: 'Payment Mode', key: 'bookingPaymentMode', formatter: (value) => value === PaymentMode.Online ? PAYMENT_MODE.ONLINE : value === PaymentMode.Offline ? PAYMENT_MODE.OFFLINE : '-', },
        {
          header: 'Received Date',
          key: 'receivedDate',
          formatter: (value) => {
            if (!value) {
              return '-';
            }

            const [datePart] = value.split(' - ');
            const date = new Date(datePart);

            return isNaN(date.getTime()) ? '-' : date;
          }
        },
        {
          header: 'Received Time',
          key: 'receivedDate',
          formatter: (value) => {
            const [, timePart] = value ? value.split(' - ') : [null, '-'];
            return timePart || '-';
          }
        },
        { header: 'Refunded ($)', key: '', formatter: (_value, item) => item.receivedAmount === 0 ? (item.bookingStatusLabel === this.bookingStatusLabel.PAID ? '-' : 0) : item.refundAmount === 0 ? '-' : myCustomPipe.transform(item.refundAmount) },
        { header: 'Refunded Date & Time', key: 'refundDate', formatter: (value) => value ? new Date(value) : '-' },
      ]
      const dataWithIndex = this.exportFile?.transactionList.map((item, index) => ({
        ...item,
        index, // Add the index to each row
      }));

      return CommonFunctions.exportToExcel(dataWithIndex, headers, 'Transactions_export_')
    }
  }

  onSelectionChange(event) {
    this.showAsterisk = false;
    const selectedValues = event.value as string[];
    if (selectedValues.includes('all')) {
      if (selectedValues?.length === this.companyListWithAll?.length) {
        return;
      }
      this.selectedCompanies = this.companyList.map(lot => lot.id);
    } else {
      const index = this.selectedCompanies.indexOf('all');
      if (index !== -1) {
        this.selectedCompanies.splice(index, 1);
      }
      this.selectedCompanies = selectedValues;
    }
    this.getParkingLotByStatesName();
  }

onPageChange(event) {
    this.first = event.first;
    this.pageNumber = event.page + 1;
    CommonFunctions.scrollTopRow('top');
    this.fetchData();
  }
  numbersOnlyValidator(event: any) {
    const pattern = /^[0-9\-]*$/;
    if (!pattern.test(event.target.value)) {
      event.target.value = event.target.value.replace(/[^0-9\-]/g, "");
    }
  }
}
